import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [tokenError, setTokenError] = useState("");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setTokenError("No reset token provided");
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } else {
        setTokenError(response.data.reason || "Invalid token");
      }
    } catch (err) {
      setTokenError("Failed to verify token");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-container">
        <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-screen">
          <div className="auth-card w-full max-w-md p-8 text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying reset token...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-screen">
        <div className="auth-card w-full max-w-md p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/assets/lsbp-logo.png" 
              alt="The Lemonade Stand"
              className="mx-auto mb-4"
              style={{ maxWidth: "150px", height: "auto" }}
            />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {success ? "Password Reset!" : tokenValid ? "Reset Password" : "Invalid Link"}
            </h1>
          </div>

          {/* Success State */}
          {success && (
            <div className="text-center">
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-6 rounded-lg mb-6" data-testid="success-message">
                <div className="text-4xl mb-3">✅</div>
                <p className="font-semibold mb-2">Password Changed Successfully!</p>
                <p className="text-sm">You can now sign in with your new password.</p>
              </div>
              
              <button
                onClick={() => navigate("/login")}
                className="lemon-button px-8 py-3 rounded-lg text-white font-bold"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </button>
            </div>
          )}

          {/* Invalid Token State */}
          {!success && !tokenValid && (
            <div className="text-center">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-6 rounded-lg mb-6" data-testid="error-message">
                <div className="text-4xl mb-3">❌</div>
                <p className="font-semibold mb-2">Invalid or Expired Link</p>
                <p className="text-sm">{tokenError}</p>
              </div>
              
              <p className="text-gray-600 mb-4">
                This password reset link is no longer valid. Please request a new one.
              </p>
              
              <Link 
                to="/forgot-password" 
                className="lemon-button inline-block px-8 py-3 rounded-lg text-white font-bold"
                data-testid="request-new-link-btn"
              >
                Request New Link
              </Link>
            </div>
          )}

          {/* Reset Form */}
          {!success && tokenValid && (
            <>
              <p className="text-center text-gray-600 mb-6">
                Enter a new password for <strong>{tokenEmail}</strong>
              </p>

              {/* Error Message */}
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="form-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="reset-password-form">
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">New Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="••••••••"
                    minLength={6}
                    required
                    data-testid="password-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="••••••••"
                    minLength={6}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </>
          )}

          {/* Back to Login */}
          {!success && (
            <div className="text-center mt-6">
              <Link to="/login" className="text-gray-500 hover:text-gray-700" data-testid="back-to-login-link">
                ← Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
